// shared helpers: DOM lookup, escaping, the CSRF token, icons, and the one-floating-layer-at-a-time rule.

/** Closes one floating layer (a menu, a sheet, a picker). */
export type CloseLayer = () => void;

/** The first element matching a selector, typed by the caller (`$<HTMLInputElement>('#code')`). */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- the caller names the element type it expects ($<HTMLInputElement>(…))
export const $ = <E extends Element = HTMLElement>(s: string, r: ParentNode = document): E | null =>
  r.querySelector<E>(s);
/** Every element matching a selector, as an array. */
export const $$ = <E extends Element = HTMLElement>(s: string, r: ParentNode = document): E[] => [
  ...r.querySelectorAll<E>(s),
];
export const meta = (n: string): string | undefined =>
  document.querySelector<HTMLMetaElement>(`meta[name="${n}"]`)?.content;
export const SPRITE = meta('ds-sprite') || '/_content/Soren.Platform.UI/ds/icons.svg'; // the layouts always write the meta (DsAssets.Sprite)
/** T-171 (packaging): a bank's mark — a platform file next to the sprite (DsAssets.BankMark), whatever the app's own wwwroot holds. */
export const bankMark = (key: string): string =>
  SPRITE.replace(/[?#].*$/, '').replace(/icons\.svg$/, '') + 'marks/banks/' + encodeURIComponent(key) + '.svg';
export const esc = (s: unknown): string =>
  String(s ?? '').replace(
    /[&<>"']/g,
    c => (({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }) as Record<string, string>)[c],
  );
export const fa = (s: unknown): string => String(s); // the FD font shows Persian digits; values stay Latin
export const reduceMotion = (): boolean => matchMedia('(prefers-reduced-motion: reduce)').matches;
export const narrow = (w: number): boolean => matchMedia(`(max-width:${w}px)`).matches;
export const csrf = (): string =>
  $<HTMLMetaElement>('meta[name="ds-csrf"]')?.content ||
  $<HTMLInputElement>('input[name="__RequestVerificationToken"]')?.value ||
  '';

export function icon(name: string, cls = ''): string {
  return `<svg class="ds-ico ${cls}" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><use href="${SPRITE}#${name}"></use></svg>`;
}
export function play(el: Element | null | undefined): void {
  if (!el || reduceMotion()) return;
  el.classList.remove('ds-draw');
  void el.getBoundingClientRect();
  el.classList.add('ds-draw');
}

// ---------------- one floating layer at a time ----------------
const layers = new Set<CloseLayer>();
// T-072: while a floating layer is open on a phone (a sheet: select, user menu, notifications, date picker, menus), the
// page behind it is dimmed and blurred (html.ds-dim, ds.css) so the sheet reads as modal. The drawer has its own scrim
// and the menu-search list sits under its field, so they do not dim.
const dimmed = new Set<CloseLayer>();
const dimSync = (): boolean => document.documentElement.classList.toggle('ds-dim', dimmed.size > 0);
export function openLayer(close: CloseLayer, dim = true): void {
  [...layers].forEach(c => c !== close && c());
  layers.add(close);
  if (dim) {
    dimmed.add(close);
    dimSync();
  }
}
export function closeLayer(close: CloseLayer): void {
  layers.delete(close);
  if (dimmed.delete(close)) dimSync();
}
// T-074: a grid fragment swap removes the menus it owned; whatever floated over the old fragment closes first
export function closeLayers(): void {
  [...layers].forEach(c => c());
  layers.clear();
  dimmed.clear();
  dimSync();
}
document.addEventListener('keydown', e => {
  if (e.key === 'Escape' && layers.size) {
    [...layers].pop()!();
  }
});
